import { ajax } from "discourse/lib/ajax";
import DiscourseRoute from "discourse/routes/discourse";
import { eventDetailRequest } from "../lib/dec-event-url";

const CHAT_TRANSCRIPT_PAGE_SIZE = 20;
const DISCUSSION_INLINE_POST_LIMIT = 6;

// Run an async fetcher with one retry on failure. Used to harden the
// loadX helpers below against transient ajax failures (cache, timing,
// auth-not-yet-ready) — the user previously reported a one-off
// "Discussion could not be loaded" that resolved on manual refresh.
// We wait a small jittered delay before the retry so we don't immediately
// hit the same transient condition.
async function fetchWithRetry(label, fetcher) {
  try {
    return await fetcher();
  } catch (firstError) {
    // eslint-disable-next-line no-console
    console.warn(`[discourse-events-calendar] ${label} fetch failed, retrying once:`, firstError);
    await new Promise((resolve) => setTimeout(resolve, 250 + Math.random() * 250));
    try {
      return await fetcher();
    } catch (secondError) {
      // eslint-disable-next-line no-console
      console.error(`[discourse-events-calendar] ${label} fetch failed after retry:`, secondError);
      throw secondError;
    }
  }
}

function extractErrorMessage(error) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  const errors = responseJSON?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(" ");
  }

  if (typeof errors === "string") {
    return errors;
  }

  if (responseJSON?.error) {
    return responseJSON.error;
  }

  if (responseJSON?.message) {
    return responseJSON.message;
  }

  return "Event could not be loaded.";
}

function normalizeEventResponse(response) {
  return response?.event || response || null;
}

function normalizeRoomsResponse(response) {
  return Array.isArray(response?.rooms) ? response.rooms : [];
}

function normalizeLocationsResponse(response) {
  return Array.isArray(response?.locations) ? response.locations : [];
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function flattenCategory(category, parentName = "") {
  const label = parentName ? `${parentName} / ${category.name}` : category.name;
  const rows = [
    {
      id: category.id,
      name: label,
      raw_name: category.name,
      slug: category.slug,
      color: category.color,
      text_color: category.text_color,
      parent_category_id: category.parent_category_id || null,
    },
  ];

  asArray(category.subcategory_list).forEach((subcategory) => {
    rows.push(...flattenCategory(subcategory, label));
  });

  return rows;
}

function normalizeCategoriesResponse(response) {
  const rows = [];
  const seenCategoryIds = new Set();

  asArray(response?.category_list?.categories || response?.categories)
    .flatMap((category) => flattenCategory(category))
    .forEach((category) => {
      const id = `${category.id}`;

      if (seenCategoryIds.has(id)) {
        return;
      }

      seenCategoryIds.add(id);
      rows.push(category);
    });

  const categoriesById = new Map(rows.map((category) => [`${category.id}`, category]));

  return rows.map((category) => ({
    ...category,
    name: categoryDisplayName(category, categoriesById),
  }));
}

function categoryBaseName(category) {
  return category?.raw_name || `${category?.name || ""}`.split(" / ").pop();
}

function categoryDisplayName(category, categoriesById, seenCategoryIds = new Set()) {
  const id = `${category?.id || ""}`;

  if (!category?.parent_category_id || seenCategoryIds.has(id)) {
    return categoryBaseName(category);
  }

  seenCategoryIds.add(id);
  const parent = categoriesById.get(`${category.parent_category_id}`);

  if (!parent) {
    return category.name;
  }

  return `${categoryDisplayName(parent, categoriesById, seenCategoryIds)} / ${categoryBaseName(category)}`;
}

function normalizeAttendeesResponse(response) {
  return {
    attendees: Array.isArray(response?.attendees) ? response.attendees : [],
    canViewAllStatuses: response?.can_view_all_statuses === true,
    loadError: "",
  };
}

function decodeHtmlEntities(value) {
  return value
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function excerptFromCooked(cooked) {
  const text = decodeHtmlEntities(`${cooked || ""}`.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= 260) {
    return text;
  }

  return `${text.slice(0, 257).trim()}...`;
}

function normalizeDiscussionResponse(response) {
  const posts = Array.isArray(response?.post_stream?.posts) ? response.post_stream.posts : [];
  const topicId = response?.id;
  const details = response?.details || {};

  return {
    topic: topicId
      ? {
          id: topicId,
          title: response.title || "Discussion",
          postsCount: response.posts_count || posts.length,
          replyCount: response.reply_count || 0,
      canCreatePost: details.can_create_post !== false,
        }
      : null,
    posts: posts
      .filter((post) => post.post_number !== 1)
      .slice(0, DISCUSSION_INLINE_POST_LIMIT)
      .map((post) => ({
        id: post.id,
        username: post.username || "",
        name: post.name || "",
        avatar_template: post.avatar_template,
        cooked: post.cooked || "",
        excerpt: excerptFromCooked(post.cooked),
        postNumber: post.post_number,
        createdAt: post.created_at,
      })),
    loadError: "",
  };
}

function chatTranscriptErrorMessage(error) {
  const status = error?.jqXHR?.status || error?.status;

  if (status === 403) {
    return "Chat transcript is not available to your account.";
  }

  if (status === 404) {
    return "Chat transcript is no longer available.";
  }

  return "Chat transcript could not be loaded.";
}

function normalizeChatUser(user) {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    username: user.username || "",
    name: user.name || "",
    avatar_template: user.avatar_template,
  };
}

function normalizeChatMessage(message) {
  const user = normalizeChatUser(message?.user);

  return {
    id: message?.id,
    message: message?.message || message?.excerpt || "",
    cooked: message?.cooked || "",
    excerpt: message?.excerpt || "",
    createdAt: message?.created_at,
    deletedAt: message?.deleted_at,
    user,
  };
}

function normalizeChatTranscriptResponse(threadResponse, messagesResponse) {
  const thread = threadResponse?.thread || null;
  const messages = [];
  const transcriptMessages = Array.isArray(messagesResponse?.messages)
    ? messagesResponse.messages
    : [];
  const seenMessageIds = new Set();

  [thread?.original_message, ...transcriptMessages].forEach((message) => {
    if (!message?.id || seenMessageIds.has(message.id)) {
      return;
    }

    seenMessageIds.add(message.id);
    messages.push(normalizeChatMessage(message));
  });

  return {
    thread: thread
      ? {
          id: thread.id,
          title: thread.title || "",
          status: thread.status || "",
          replyCount: thread.reply_count || 0,
        }
      : null,
    messages,
    canLoadMorePast: messagesResponse?.meta?.can_load_more_past === true,
    canLoadMoreFuture: messagesResponse?.meta?.can_load_more_future === true,
    loadError: "",
  };
}

function normalizeChatChannelResponse(response) {
  const channel = response?.channel || response;
  if (!channel?.id) {
    return null;
  }

  const membership = channel.current_user_membership || {};
  const meta = channel.meta || {};

  return {
    id: channel.id,
    title: channel.title || channel.name || "Chat channel",
    slug: channel.slug || "",
    status: channel.status || "",
    canJoin: meta.can_join_chat_channel !== false,
    membership,
    following: membership.following === true,
  };
}

async function loadDiscussionForEvent(event) {
  if (!event?.topic_id) {
    return { topic: null, posts: [], loadError: "" };
  }

  try {
    const response = await fetchWithRetry("discussion", () =>
      ajax(`/t/-/${event.topic_id}.json`)
    );
    return normalizeDiscussionResponse(response);
  } catch {
    return {
      topic: null,
      posts: [],
      loadError: "Discussion could not be loaded.",
    };
  }
}

async function loadChatChannelForEvent(event) {
  if (!event?.chat_channel_id) {
    return { channel: null, loadError: "" };
  }

  const channelId = encodeURIComponent(event.chat_channel_id);

  try {
    const response = await fetchWithRetry("chat-channel", () =>
      ajax(`/chat/api/channels/${channelId}`)
    );
    return { channel: normalizeChatChannelResponse(response), loadError: "" };
  } catch {
    return { channel: null, loadError: "" };
  }
}

async function loadChatTranscriptForEvent(event) {
  if (!event?.chat_channel_id || !event?.chat_thread_id) {
    return {
      thread: null,
      messages: [],
      canLoadMorePast: false,
      canLoadMoreFuture: false,
      loadError: "",
    };
  }

  const channelId = encodeURIComponent(event.chat_channel_id);
  const threadId = encodeURIComponent(event.chat_thread_id);
  const messagesData = {
    page_size: CHAT_TRANSCRIPT_PAGE_SIZE,
    fetch_from_first_message: true,
  };

  try {
    const [threadResponse, messagesResponse] = await fetchWithRetry("chat-transcript", () =>
      Promise.all([
        ajax(`/chat/api/channels/${channelId}/threads/${threadId}`),
        ajax(`/chat/api/channels/${channelId}/threads/${threadId}/messages`, {
          data: messagesData,
        }),
      ])
    );

    return normalizeChatTranscriptResponse(threadResponse, messagesResponse);
  } catch (error) {
    return {
      thread: null,
      messages: [],
      canLoadMorePast: false,
      canLoadMoreFuture: false,
      loadError: chatTranscriptErrorMessage(error),
    };
  }
}

export default class DiscourseEventsCalendarEventRoute extends DiscourseRoute {
  queryParams = {
    occurrence_id: { refreshModel: true },
    occurrence_key: { refreshModel: true },
  };

  async model(params, transition) {
    try {
      const queryParams = transition?.to?.queryParams || {};
      const eventRequest = eventDetailRequest(params.event_id, queryParams);
      const eventResponse = await ajax(eventRequest.url, eventRequest.options);
      const event = normalizeEventResponse(eventResponse);
      const roomsRequest = ajax("/calendar/rooms.json").catch(() => ({ rooms: [] }));
      const locationsRequest = ajax("/calendar/locations.json").catch(() => ({ locations: [] }));
      const categoriesRequest = ajax("/categories.json", {
        data: { include_subcategories: true },
      }).catch(() => ({ categories: [] }));
      const attendeesRequest = fetchWithRetry("attendees", () =>
        ajax(`/calendar/events/${params.event_id}/attendees.json`)
      )
        .then(normalizeAttendeesResponse)
        .catch(() => ({
          attendees: [],
          canViewAllStatuses: false,
          loadError: "Attendees could not be loaded.",
        }));
      const discussionRequest = loadDiscussionForEvent(event);
      const chatChannelRequest = loadChatChannelForEvent(event);
      const chatTranscriptRequest = loadChatTranscriptForEvent(event);
      const [
        roomsResponse,
        locationsResponse,
        categoriesResponse,
        attendeesResponse,
        discussionResponse,
        chatChannelResponse,
        chatTranscriptResponse,
      ] = await Promise.all([
        roomsRequest,
        locationsRequest,
        categoriesRequest,
        attendeesRequest,
        discussionRequest,
        chatChannelRequest,
        chatTranscriptRequest,
      ]);

      return {
        event,
        placesEnabled: eventResponse?.places_enabled === true,
        ai_image_generation_available: eventResponse?.ai_image_generation_available === true,
        rooms: normalizeRoomsResponse(roomsResponse),
        locations: normalizeLocationsResponse(locationsResponse),
        categories: normalizeCategoriesResponse(categoriesResponse),
        attendees: attendeesResponse.attendees,
        canViewAllAttendeeStatuses: attendeesResponse.canViewAllStatuses,
        attendeeLoadError: attendeesResponse.loadError,
        discussionTopic: discussionResponse.topic,
        discussionPosts: discussionResponse.posts,
        discussionLoadError: discussionResponse.loadError,
        chatChannel: chatChannelResponse.channel,
        chatChannelLoadError: chatChannelResponse.loadError,
        chatThread: chatTranscriptResponse.thread,
        chatMessages: chatTranscriptResponse.messages,
        chatCanLoadMorePast: chatTranscriptResponse.canLoadMorePast,
        chatCanLoadMoreFuture: chatTranscriptResponse.canLoadMoreFuture,
        chatLoadError: chatTranscriptResponse.loadError,
        loadError: "",
      };
    } catch (error) {
      return {
        event: null,
        placesEnabled: false,
        ai_image_generation_available: false,
        rooms: [],
        locations: [],
        categories: [],
        attendees: [],
        canViewAllAttendeeStatuses: false,
        attendeeLoadError: "",
        discussionTopic: null,
        discussionPosts: [],
        discussionLoadError: "",
        chatChannel: null,
        chatChannelLoadError: "",
        chatThread: null,
        chatMessages: [],
        chatCanLoadMorePast: false,
        chatCanLoadMoreFuture: false,
        chatLoadError: "",
        loadError: extractErrorMessage(error),
      };
    }
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    controller.syncEventFromModel(model);
  }

  titleToken() {
    return this.currentModel?.event?.title || "Event";
  }
}
